<template>
  <div v-if="!isLoading" class="ticket-create">
    <ViewHeader
      :title="
        editId === 'create'
          ? 'Create Ticket'
          : allowEdit
          ? `Edit Ticket number ${editId}`
          : `Ticket number ${editId}`
      "
    >
      <CButton
        v-if="!ticketClosed && allowEdit"
        color="primary"
        class="ra-button"
        @click="saveTicket"
        data-cy="save-ticket"
      >
        <Icon name="plus" class="mr-1" v-if="!editId" />
        <Icon name="save" class="mr-1" v-else />
        {{ editId !== "create" ? "Save" : "Create" }}
      </CButton>
      <CButton
        v-if="!ticketClosed && editId !== 'create' && !allowEdit"
        color="primary"
        class="ra-button"
        @click="allowEdit = true"
      >
        <Icon name="save" class="mr-1" />
        Edit
      </CButton>
    </ViewHeader>

    <div class="d-flex justify-content-between w-100 gap-15">
      <div class="main-inputs flex-grow-1">
        <InputComponent
          :disabled="ticketClosed || !allowEdit"
          v-for="formField in Object.values(mainFields)"
          class="mb-3"
          :key="formField.name"
          :formField="formField"
          :incorrectInputValues="incorrectInputValues.includes(formField.name)"
        />
      </div>
      <div class="entity-inputs">
        <template v-for="formField in Object.values(entityFields)">
          <InputComponent
            v-if="!['text', 'number'].includes(formField.type)"
            :disabled="ticketClosed || !allowEdit"
            :formField="formField"
            :key="`${formField.name}_${
              formField.value && Array.isArray(formField.value)
                ? formField.value.join()
                : formField.value
            }`"
            :incorrectInputValues="
              incorrectInputValues.includes(formField.name)
            "
            @select="selectHandler"
            @remove="removeHandler"
          >
            <template #afterList><span></span></template>
          </InputComponent>
          <div v-else>
            <label
              v-if="(formField.label || formField.name) && !formField.hideLabel"
              class="font-weight-bold text-capitalize"
              :class="[
                incorrectInputValues.includes(formField.name)
                  ? 'text-danger'
                  : 'text-muted',
                { 'mr-4': formField.inlineLabel && formField.options },
              ]"
            >
              {{ formField.label || formField.name.replaceAll("_", " ") }}
              <span
                v-if="formField.required"
                v-c-tooltip="'This field is required'"
              >
                *
              </span>
            </label>
            <RaInput
              size="sm"
              class="filter-input"
              :name="formField.name"
              v-model.number="formField.value"
              :type="formField.type"
              :disabled="ticketClosed || !allowEdit"
              :autocomplete="formField.autocomplete"
              @keypress="validateNumber"
            />
          </div>
        </template>
      </div>
    </div>
    <div v-if="editId !== 'create' && existingTicketData" class="mt-3">
      <FollowUp
        allowHtmlContent
        :data="changelog"
        v-if="changelog.length"
        class="mb-3"
        @click="handleLinksForDownload($event)"
      />
      <InputComponent :formField="commentField" :disabled="ticketClosed" />
      <div class="d-flex align-items-center justify-content-end my-5 gap-5">
        <CButton
          v-if="!ticketClosed"
          color="primary"
          class="ra-button"
          @click="() => handleComment('Closed')"
          data-cy="comment-and-close"
        >
          Close ticket and comment
        </CButton>
        <CButton
          color="primary"
          class="ra-button"
          @click="() => handleComment('Open')"
        >
          {{ ticketClosed ? "Open ticket" : "Comment" }}
        </CButton>
      </div>
    </div>
  </div>
  <DotsLoader v-else></DotsLoader>
</template>

<script>
import moment from "moment";

import { EntitiesService } from "@/services/entity";
import { LabelsService } from "@/services/labels";
import { TicketsService } from "@/services/tickets";
import { ActiveUser } from "@/services/user";

import InputComponent from "@/components/InputComponent";
import { UsersMixin } from "@/mixins/users";
import { decode } from "querystring";
import { handleLinksForDownload } from "@/utils/request";

export default {
  name: "TicketsCreate",
  components: { InputComponent },
  mixins: [UsersMixin],
  data() {
    return {
      editId: null,
      existingTicketData: null,
      isLoading: false,
      allowEdit: true,
      entities: [],
      incorrectInputValues: [],
      availableServices: {},
      commentField: {
        name: "comment",
        value: "",
        type: "textarea",
        required: true,
        file: {
          id: 2,
          query: {
            ticket_id: null,
          },
        },
      },
      mainFields: {
        title: {
          name: "title",
          placeholder: "Please complete the title",
          value: "",
          type: "text",
          required: true,
          fullWidth: true,
        },
        description: {
          name: "description",
          placeholder:
            "Please add description of the context and what need to be done ",
          value: "",
          type: "textarea",
          fullWidth: true,
          file: {
            id: 2,
            query: {
              ticket_id: null,
            },
          },
        },
      },
      entityFields: {
        entity_code: {
          name: "entity_code",
          placeholder: "Which entity is concern ?",
          type: "select",
          value: null,
          options: [],
          searchable: true,
          required: true,
        },
        services: {
          name: "services",
          type: "select",
          value: null,
          options: [],
          searchable: true,
          required: true,
        },
        contexte: {
          label: "Context",
          name: "contexte",
          type: "select",
          value: null,
          options: [],
          searchable: true,
          required: true,
        },
        label: {
          name: "label",
          type: "multiselect",
          placeholder: "What is about ?",
          value: [],
          options: [],
          required: true,
        },
        assignee: {
          name: "assignee",
          type: "select",
          placeholder: "Please assign to ",
          value: null,
          searchable: true,
          options: [],
          required: true,
        },
        due_date: {
          dateFormat: "YYYY-MM-DD",
          name: "due_date",
          placeholder: "Select date",
          value: null,
          type: "date",
          min: moment().format("YYYY-MM-DD"),
        },
        amount: {
          name: "amount",
          type: "text",
          value: null,
        },
      },
      ticketQueryParams: null,
    };
  },
  async mounted() {
    this.isLoading = true;
    await this.getOrCreateTicketData();
    await this.getTicketQueryParams();
    this.isLoading = false;
  },
  computed: {
    changelog() {
      return (
        this.existingTicketData?.comments?.map((comment) => {
          return {
            author: comment.user,
            content: comment.comments,
            type: comment.is_followup ? "comment" : "recommendation",
            createdDate: moment(comment.updated_at).toDate(),
            label: comment.is_followup ? "Comment" : "Ticket created",
          };
        }) || []
      );
    },
    ticketClosed() {
      return this.editId && this.existingTicketData?.ticket_status === "Closed";
    },
    postBody() {
      return {
        title: this.mainFields.title.value,
        description: this.mainFields.description.value,
        entity_code: this.entityFields.entity_code.value,
        services: this.entityFields.services.value,
        contexte: this.entityFields.contexte.value,
        label: this.entityFields.label.value,
        assignee: this.entityFields.assignee.value,
        due_date: this.entityFields.due_date.value,
        amount: parseFloat(this.entityFields.amount.value),
      };
    },
    selectedFinancialGroups() {
      const { value } = this.entityFields.entity_code;
      if (value === "All")
        return Array.from(new Set(this.entities.map((e) => e.group)));
      const entityIndex = this.entities.findIndex((e) => e.value === value);
      if (entityIndex !== -1) return this.entities[entityIndex].group;
      return [];
    },
  },
  methods: {
    handleLinksForDownload,
    async getTicketQueryParams() {
      let query = this.$route.query || null;
      let ticket_data = query ? query["ticket_data"] : null;
      if (ticket_data) {
        ticket_data = decode(atob(ticket_data));
        this.ticketQueryParams = ticket_data;
        Object.keys(this.mainFields).forEach((k) => {
          if (ticket_data.hasOwnProperty(k)) {
            this.mainFields[k].value = ticket_data[k];
          }
        });
        Object.keys(this.entityFields).forEach((k) => {
          if (ticket_data.hasOwnProperty(k)) {
            if (k === "label")
              this.entityFields[k].value = ticket_data[k].split(",");
            else this.entityFields[k].value = ticket_data[k];
          }
        });
      }
      await this.handleSuggestedMembers();
      if (ticket_data)
        this.entityFields.assignee.value =
          this.entityFields.assignee.options[0].value || "";
    },
    validateNumber(event) {
      let keyCode = event.keyCode;
      if (keyCode < 45 || keyCode > 57) {
        event.preventDefault();
      }
    },
    async getOrCreateTicketData() {
      this.editId = this.$route.params.id || "create";
      this.mainFields.description.file.query.ticket_id = this.editId;
      this.commentField.file.query.ticket_id = this.editId;
      if (this.editId !== "create") {
        this.editId = Number(this.editId);
        const { res, data } = await TicketsService.getTicket(this.editId);
        if (res.status === 404) return this.$router.push("/404");
        if (this.$route.query.duplicate) {
          this.editId = "create";
          delete data.ticket_status;
        } else {
          this.allowEdit = false;
        }
        this.existingTicketData = data;
        if (!this.existingTicketData.assignee) {
          this.entityFields.assignee.required = false;
        }
        this.existingTicketData.comments =
          this.existingTicketData?.comments || [];
      }
      const possibleOptions = await EntitiesService.getOptions();
      this.entityFields.contexte.options =
        await EntitiesService.getContexteOptions();

      Object.values(possibleOptions.categories).forEach((field) => {
        this.availableServices[field["label"]] = field["field_name"];
      });
      this.entityFields.services.options = Object.keys(
        this.availableServices
      ).map((services) => {
        return {
          label: services,
          value: services,
        };
      });
      this.entityFields.services.options.sort((a, b) => {
        return a.value.localeCompare(b.value);
      });
      await this.populateFields();
    },
    async populateLabels() {
      const labels = await LabelsService.getLabels();
      this.entityFields.label.options = labels.map((label) => {
        return {
          value: label.name,
          label: label.name,
        };
      });
      this.existingTicketData?.label.forEach((label) => {
        if (!this.entityFields.label.options.find((e) => e.value === label)) {
          this.entityFields.label.options.push({ value: label, label: label });
        }
      });
    },
    async populateEntities() {
      this.entities = (await EntitiesService.getAllEntities()).map((entity) => {
        return {
          value: entity.code,
          label: entity.code,
          group: entity.group,
        };
      });
      let entitiesOnTop = [
        "ALL",
        "DFF",
        "OFFSHORE",
        "CORPORATE",
        "LUXEL",
        "EGM",
      ];
      const restEntities = this.entities.filter(
        (entity) => !entitiesOnTop.includes(entity.value)
      );
      entitiesOnTop = entitiesOnTop
        .map((e) => {
          const entityIndex = this.entities.findIndex(
            (entity) => entity.value === e
          );
          if (entityIndex !== -1) return this.entities[entityIndex];
        })
        .filter((e) => e);
      this.entityFields.entity_code.options = [
        ...entitiesOnTop,
        ...restEntities.sort((a, b) => {
          return a.label.localeCompare(b.label);
        }),
      ];
    },
    async populateFields() {
      await this.populateLabels();
      await this.populateEntities();
      this.entityFields.assignee.options = await this.populateMembers();
      await this.handleSuggestedMembers();

      if (this.existingTicketData) {
        Object.keys(this.postBody).map(async (property) => {
          const fieldType = ["title", "description"].includes(property)
            ? "mainFields"
            : "entityFields";
          if (this.existingTicketData[property])
            this[fieldType][property].value = this.existingTicketData[property];
        });
        await this.handleSuggestedMembers();
      }

      this.isLoading = false;
    },
    async selectHandler(item, fieldName) {
      if (this.entityFields[fieldName].value === item.value)
        return this.removeHandler(item.value, fieldName);

      if (this.entityFields[fieldName].type !== "multiselect") {
        this.entityFields[fieldName].value = item.value;
      }

      if (["entity_code", "services"].includes(fieldName)) {
        await this.handleSuggestedMembers();
      }
    },
    async handleSuggestedMembers(isRemove = false) {
      const entity = this.entityFields.entity_code.value;
      const services = this.availableServices[this.entityFields.services.value];
      const suggestedMembers = await this.getSuggestedMembers(entity, services);
      const memberAbbreviations = suggestedMembers.map((member) => member.name);
      this.entityFields.assignee.options = await this.populateMembers();
      this.entityFields.assignee.options.sort((a, b) => {
        if (memberAbbreviations.includes(a.value)) return -1;
        return a.value.localeCompare(b.value);
      });
      if (isRemove)
        this.entityFields.assignee.options = this.createMembersSelector(
          this.members
        );
      if (suggestedMembers && suggestedMembers.length) {
        this.entityFields.assignee.options =
          this.entityFields.assignee.options.filter(
            (member) => !memberAbbreviations.includes(member.name)
          );
        this.entityFields.assignee.options = [
          ...suggestedMembers.map(({ name }) => {
            const suggestedName = this.getSuggestedMemberStr(name);
            return {
              name: name,
              value: name,
              label: suggestedName,
            };
          }),
          ...this.entityFields.assignee.options,
        ];
      }
    },
    async removeHandler(item, fieldName) {
      const isMultiselect = this.entityFields[fieldName].type === "multiselect";

      if (isMultiselect) {
        const filteredValues = [...this.entityFields[fieldName].value].filter(
          (fieldValue) => fieldValue !== item && fieldValue !== "All"
        );
        this.entityFields[fieldName].value = filteredValues;
      } else {
        this.entityFields[fieldName].value = null;
      }

      if (["entity_code", "services"].includes(fieldName)) {
        await this.handleSuggestedMembers(true);
      }
    },
    async handleComment(ticket_status = "Open") {
      const validationPassed =
        this.commentField.value && this.commentField.value !== "";
      if (!this.ticketClosed && !validationPassed) {
        return alert("Comment can not be empty");
      }
      let commentsBody = {
        ticket_status,
      };
      if (ticket_status === "Closed") {
        commentsBody.closed_at = new Date();
      }
      if (!this.ticketClosed) {
        commentsBody.comments = [
          ...(this.existingTicketData.comments || []),
          {
            is_followup: true,
            comments: this.commentField.value,
            user: ActiveUser.get()?.email,
          },
        ];
      }
      await this.saveTicket(commentsBody, false);
      const { res, data } = await TicketsService.getTicket(this.editId);
      this.existingTicketData = data;
      this.commentField.value = "";
    },
    ticketDataCorrect() {
      this.incorrectInputValues = [];
      const allFields = [
        ...Object.values(this.mainFields),
        ...Object.values(this.entityFields),
      ];
      allFields.forEach((formField) => {
        if (
          formField.required &&
          (!formField.value || !formField.value?.length)
        )
          this.incorrectInputValues.push(formField.name);
        else if (
          formField.options?.length &&
          formField.type === "multiselect"
        ) {
          const namedOptions = formField.options.map((option) =>
            option.value.replace(" (suggested)", "")
          );
          if (
            !formField.value.every((option) =>
              namedOptions.includes(option.replace(" (suggested)", ""))
            )
          )
            this.incorrectInputValues.push(formField.name);
        }
      });
      return this.incorrectInputValues.length === 0;
    },
    async saveTicket(overrideValues = {}, shouldRedirect = true) {
      if (this.editId !== "create") shouldRedirect = false;
      if (!this.ticketDataCorrect())
        return alert("Please fill in all required fields");

      const payload = {
        ...this.postBody,
        ...overrideValues,
      };
      if (this.editId === "create" && this.ticketQueryParams) {
        payload["test_code"] = this.ticketQueryParams.test_code;
        payload["quarter_name"] = this.ticketQueryParams.quarter_name;
      }
      const { res, data } = await TicketsService.editOrCreateTicket(
        this.editId,
        payload
      );
      if (res.status === 200) {
        if (shouldRedirect) {
          await this.$router.push({
            name: "Create task",
            params: { id: data },
          });
        } else {
          await this.getOrCreateTicketData();
        }
      } else if (res.status !== 200) alert("Something went wrong");
    },
  },
  beforeDestroy() {
    this.$store.commit("set", ["uploadedFiles", []]);
  },
};
</script>

<style lang="scss">
.ticket-create {
  img {
    max-width: 100%;
  }
}
.entity-inputs {
  min-width: 350px;
}

.main-inputs {
  max-width: calc(100% - 400px);
}
strong {
  font-weight: 900;
}
</style>
